import {
  MAX_FOLDER_DEPTH,
  ROOT_FOLDER_ID,
  type FileNode,
  type FileSystemNode,
  type FolderNode,
} from '../types/fileSystem.js';
import {
  getFileFolderId,
  getNodeName,
  getNodeParentId,
  isFolderNode,
} from './fileSystemNode.js';

// FYI: containment is client state until the API grows a folder tree, so every
// restriction lives here. Once it lands, keep this as the optimistic pre-check
// and let the server be the authority.
export type MoveRejection =
  | 'missing-target'
  | 'self'
  | 'descendant'
  | 'same-parent'
  | 'max-depth'
  | 'name-taken'
  | 'file-not-ready';

export type MoveVerdict =
  | { allowed: true }
  | { allowed: false; reason: MoveRejection };

export type FileSystemSnapshot = {
  folders: FolderNode[];
  files: FileNode[];
  /** fileId -> folderId. A file missing from the map sits in the root. */
  fileLocations: Record<string, string>;
};

const ALLOWED: MoveVerdict = { allowed: true };

const deny = (reason: MoveRejection): MoveVerdict => ({
  allowed: false,
  reason,
});

const findFolder = (folders: FolderNode[], folderId: string) =>
  folders.find((folder) => folder.id === folderId);

/** Root is 0, a folder parented to the root is 1, and so on. */
export const getFolderDepth = (folders: FolderNode[], folderId: string) => {
  const seen = new Set<string>();
  let cursor = findFolder(folders, folderId);
  let depth = 0;

  while (cursor && !seen.has(cursor.id)) {
    seen.add(cursor.id);
    depth += 1;
    cursor = findFolder(folders, cursor.parentId);
  }

  return depth;
};

/** How many levels the folder carries below itself; a leaf folder is 0. */
export const getFolderHeight = (folders: FolderNode[], folderId: string) => {
  const walk = (id: string, seen: Set<string>): number => {
    if (seen.has(id)) return 0;

    seen.add(id);

    const children = folders.filter((folder) => folder.parentId === id);

    if (!children.length) return 0;

    return 1 + Math.max(...children.map((child) => walk(child.id, seen)));
  };

  return walk(folderId, new Set<string>());
};

export const isDescendantFolder = (
  folders: FolderNode[],
  candidateId: string,
  ancestorId: string,
) => {
  const seen = new Set<string>();
  let cursor = findFolder(folders, candidateId);

  while (cursor && !seen.has(cursor.id)) {
    if (cursor.parentId === ancestorId) return true;

    seen.add(cursor.id);
    cursor = findFolder(folders, cursor.parentId);
  }

  return false;
};

const hasNameClash = (
  node: FileSystemNode,
  targetFolderId: string,
  { folders, files, fileLocations }: FileSystemSnapshot,
) => {
  const name = getNodeName(node).trim().toLowerCase();

  const siblings: FileSystemNode[] = isFolderNode(node)
    ? folders.filter((folder) => folder.parentId === targetFolderId)
    : files.filter(
        (file) => getFileFolderId(file.id, fileLocations) === targetFolderId,
      );

  return siblings.some(
    (sibling) =>
      sibling.id !== node.id &&
      getNodeName(sibling).trim().toLowerCase() === name,
  );
};

export const canMoveNode = (
  node: FileSystemNode,
  targetFolderId: string,
  snapshot: FileSystemSnapshot,
): MoveVerdict => {
  const { folders } = snapshot;

  if (
    targetFolderId !== ROOT_FOLDER_ID &&
    !findFolder(folders, targetFolderId)
  ) {
    return deny('missing-target');
  }

  // Pending documents are still being ingested; the rest of the UI already
  // refuses to act on them (see the grid's isRowSelectable).
  if (!isFolderNode(node) && node.status === 'pending') {
    return deny('file-not-ready');
  }

  if (isFolderNode(node)) {
    if (node.id === targetFolderId) return deny('self');

    if (isDescendantFolder(folders, targetFolderId, node.id)) {
      return deny('descendant');
    }
  }

  if (getNodeParentId(node, snapshot.fileLocations) === targetFolderId) {
    return deny('same-parent');
  }

  if (isFolderNode(node)) {
    const landingDepth = getFolderDepth(folders, targetFolderId) + 1;

    if (landingDepth + getFolderHeight(folders, node.id) > MAX_FOLDER_DEPTH) {
      return deny('max-depth');
    }
  }

  if (hasNameClash(node, targetFolderId, snapshot)) {
    return deny('name-taken');
  }

  return ALLOWED;
};

/**
 * All-or-nothing on purpose: a partially applied drop is hard to explain and
 * harder to undo, so one blocked node rejects the whole gesture and the caller
 * reports the first reason.
 */
export const canMoveNodes = (
  nodes: FileSystemNode[],
  targetFolderId: string,
  snapshot: FileSystemSnapshot,
): MoveVerdict => {
  if (!nodes.length) return deny('missing-target');

  for (const node of nodes) {
    const verdict = canMoveNode(node, targetFolderId, snapshot);

    if (!verdict.allowed) return verdict;
  }

  return ALLOWED;
};
